// src/components/resumo/layout/CompactPageNav.tsx
// 🎯 Navegação compacta de páginas para o centro do header

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './CompactPageNav.css';

interface CompactPageNavProps {
    currentPage: number; // Índice baseado em 0
    totalPages: number;
    onPageChange: (index: number) => void;
    darkMode?: boolean;
}

const CompactPageNav: React.FC<CompactPageNavProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    darkMode = false
}) => {
    const hasPrevious = currentPage > 0;
    const hasNext = currentPage < totalPages - 1;

    // Reset de scroll NÃO é responsabilidade deste componente — em fullscreen,
    // o container que realmente rola é um elemento interno da página (ex:
    // #rrp-scroll-container), não document.fullscreenElement; um scrollTo()
    // aqui rolava o elemento errado. As duas páginas que usam este nav
    // (ResumoReadPage e ApostilaPreviewPage) já têm um useLayoutEffect
    // chaveado em currentPageIndex que reseta o scroll do container certo
    // após a troca — essa é a única fonte de verdade agora.
    const goToPrevious = () => {
        if (hasPrevious) onPageChange(currentPage - 1);
    };

    const goToNext = () => {
        if (hasNext) onPageChange(currentPage + 1);
    };

    // ✅ Não renderizar se só tiver 1 página
    if (totalPages <= 1) {
        return null;
    }

    return (
        <div className={`compact-page-nav ${darkMode ? 'dark' : ''}`}>
            <button
                className="cpn-btn cpn-btn-prev"
                onClick={goToPrevious}
                disabled={!hasPrevious}
                title="Página Anterior"
                aria-label="Página Anterior"
            >
                <ChevronLeft size={18} />
            </button>

            <div className="cpn-indicator">
                <span className="cpn-current">{currentPage + 1}</span>
                <span className="cpn-separator">/</span>
                <span className="cpn-total">{totalPages}</span>
            </div>

            <button
                className="cpn-btn cpn-btn-next"
                onClick={goToNext}
                disabled={!hasNext}
                title="Próxima Página"
                aria-label="Próxima Página"
            >
                <ChevronRight size={18} />
            </button>
        </div>
    );
};

export default CompactPageNav;